import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, Button, Link, Typography } from '@mui/material';
import type { DriverType } from '@repo/supergraph-schemas/marketplace/types';
import { DisclosureTooltip } from '../../../../DisclosureTooltip';

type DriverListVariant = 'table' | 'simple';
type AlignValue = 'center' | 'flex-start';
type ResponsiveAlign =
  | AlignValue
  | { xs?: AlignValue; sm?: AlignValue; md?: AlignValue; lg?: AlignValue; xl?: AlignValue };

type Props = {
  drivers: DriverType[];
  variant: DriverListVariant;
  header?: string;
  showDescription?: boolean;
  align?: ResponsiveAlign;
};

export function CDDriverList({
  drivers,
  variant,
  header = 'APY',
  showDescription = variant === 'table',
  align = variant === 'table' ? 'center' : 'flex-start',
}: Props) {
  const isTable = variant === 'table';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: align,
        textAlign: isTable ? 'center' : 'left',
        gap: 1,
      }}
    >
      <Typography
        variant={isTable ? 'textSmallBold' : 'textBold'}
        sx={
          isTable
            ? { color: 'neutral.darkest', textTransform: 'uppercase', fontSize: '11px' }
            : { color: 'neutral.darker' }
        }
      >
        {header}
      </Typography>

      {drivers.map((driver, driverIndex) => (
        <Box
          key={`driver-${driver.id}-${driverIndex}`}
          sx={{
            display: 'flex',
            flexDirection: isTable ? 'column' : 'row',
            alignItems: align,
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="text"
              sx={{
                fontVariantNumeric: 'tabular-nums',
                ...(isTable ? { fontSize: '14px', marginRight: 1 } : { color: 'black' }),
              }}
            >
              {`${driver.label} ${driver.value}`}
            </Typography>

            {driver.valueTooltip ? (
              <DisclosureTooltip
                tooltipStyleOverride={
                  isTable ? { maxWidth: '275px', paddingX: 3, paddingY: 2 } : undefined
                }
                trigger={
                  isTable ? (
                    <Link aria-label="APY info" component="button" lineHeight={1}>
                      <HelpOutlineIcon
                        sx={{ width: '16px', height: '16px', color: 'neutral.dark' }}
                      />
                    </Link>
                  ) : (
                    <Button
                      aria-label="driver info"
                      variant="tooltip"
                      disableTouchRipple
                      sx={{ display: 'flex', height: '100%', justifyContent: 'center' }}
                    >
                      <HelpOutlineIcon
                        sx={{ width: '14px', height: '14px', color: 'neutral.dark' }}
                      />
                    </Button>
                  )
                }
              >
                <Typography
                  sx={isTable ? undefined : { fontSize: '0.813rem' }}
                  variant={isTable ? 'textSmall' : undefined}
                >
                  {driver.valueTooltip}
                </Typography>
              </DisclosureTooltip>
            ) : null}
          </Box>

          {showDescription && driver.description ? (
            <Typography variant="textSmall">{driver.description}</Typography>
          ) : null}
        </Box>
      ))}
    </Box>
  );
}
